function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { Children, Fragment, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Icon as IconButton } from "@churchofjesuschrist/eden-buttons";
import { GridView, Clear, ArrowBack, ChevronLeft, ChevronRight } from "@churchofjesuschrist/eden-icons";
import { spacing64, breakWidth480, spacing8 } from "@churchofjesuschrist/eden-style-constants";
import { dir } from "@churchofjesuschrist/eden-styled-helpers";
import { Text4 } from "@churchofjesuschrist/eden-text";
import HotKey from "@churchofjesuschrist/eden-hotkey";
import Mask from "@churchofjesuschrist/eden-platform-mask";
import Caption from "./Caption";
import Grid from "./Grid";
import ItemLayout from "./ItemLayout";
import Head from "./Head";
const Container = styled.div.withConfig({
  componentId: "sc-1xrexcs-0"
})(["display:grid;grid-template-rows:", " 1fr;grid-template-columns:1fr;width:100%;height:100%;overflow:auto;"], spacing64);
const NavButton = styled(IconButton).withConfig({
  componentId: "sc-1xrexcs-1"
})(["", ""], dir("rtl")`
    transform: rotate(180deg);
  `);
const HeadPiece = styled.div.withConfig({
  componentId: "sc-1xrexcs-2"
})(["display:flex;align-items:center;"]);
const HeadNav = styled.div.withConfig({
  componentId: "sc-1xrexcs-3"
})(["display:flex;@media (min-width:", "){display:none;}"], breakWidth480);

const MediaModal = _ref => {
  let {
    onClose,
    children,
    initialItemIndex = 0,
    closeKey = "Escape",
    ...props
  } = _ref;
  const items = Children.toArray(children);
  const [viewMode, setViewMode] = useState("item");
  const [currentItemIndex, setCurrentItemIndex] = useState(initialItemIndex);

  const goToGrid = () => {
    setViewMode("grid");
  };

  const goToItem = () => {
    setViewMode("item");
  };

  const handleItemSelected = index => {
    setCurrentItemIndex(index);
    setViewMode("item");
  };

  const handleClose = () => {
    onClose();
  };

  const handlePrevious = () => {
    const itemsCount = items.length;

    if (currentItemIndex === 0) {
      setCurrentItemIndex(itemsCount - 1);
    } else {
      setCurrentItemIndex(prevIndex => prevIndex - 1);
    }
  };

  const handleNext = () => {
    const itemsCount = items.length;

    if (currentItemIndex >= itemsCount - 1) {
      setCurrentItemIndex(0);
    } else {
      setCurrentItemIndex(prevIndex => prevIndex + 1);
    }
  };

  const currentItem = items[currentItemIndex];
  let previous = null;
  let next = null;
  let navAction = "";

  if (items.length > 1) {
    if (viewMode === "item") {
      navAction = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(IconButton, {
        onClick: goToGrid,
        inverted: true
      }, /*#__PURE__*/React.createElement(GridView, null)), /*#__PURE__*/React.createElement(Text4, {
        style: {
          paddingLeft: spacing8,
          paddingRight: spacing8
        },
        inverted: true
      }, currentItemIndex + 1, " / ", items.length));
    } else {
      navAction = /*#__PURE__*/React.createElement(NavButton, {
        onClick: goToItem,
        inverted: true
      }, /*#__PURE__*/React.createElement(ArrowBack, null));
    }

    previous = /*#__PURE__*/React.createElement(NavButton, {
      onClick: handlePrevious,
      inverted: true
    }, /*#__PURE__*/React.createElement(ChevronLeft, null));
    next = /*#__PURE__*/React.createElement(NavButton, {
      onClick: handleNext,
      inverted: true
    }, /*#__PURE__*/React.createElement(ChevronRight, null));
  }

  return /*#__PURE__*/React.createElement(Mask, _extends({
    active: true,
    type: "media"
  }, props), /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(Head, {
    start: /*#__PURE__*/React.createElement(HeadPiece, null, navAction),
    end: /*#__PURE__*/React.createElement(HeadPiece, null, viewMode === "item" && /*#__PURE__*/React.createElement(HeadNav, null, previous, next), /*#__PURE__*/React.createElement(IconButton, {
      onClick: handleClose,
      inverted: true,
      "data-testid": "closeButton"
    }, /*#__PURE__*/React.createElement(Clear, null)))
  }), viewMode === "item" ? /*#__PURE__*/React.createElement(ItemLayout, {
    previous: previous,
    next: next,
    main: currentItem,
    foot: /*#__PURE__*/React.createElement(Caption, {
      title: currentItem.props.title,
      caption: currentItem.props.caption,
      attribution: currentItem.props.attribution
    })
  }) : /*#__PURE__*/React.createElement(Grid, {
    items: items,
    activeIndex: currentItemIndex,
    onItemSelected: handleItemSelected
  }), closeKey !== null && /*#__PURE__*/React.createElement(HotKey, {
    sequence: [closeKey],
    onMatch: handleClose
  })));
};

MediaModal.propTypes = {
  /** Called when the close button is clicked */
  onClose: PropTypes.func.isRequired,

  /** Used to define what the media items in the modal are. Should be one or more of ModalImage and ModalVideo */
  children: PropTypes.node.isRequired,

  /** Defines which item in the gallery should be active when it first opens. */
  initialItemIndex: PropTypes.number,

  /** Defines which key, when pressed, will close the modal; a value of 'null' disables the feature */
  closeKey: PropTypes.string
};
export default MediaModal;